/**
 * TokenService - Secure token management with memory-only storage
 *
 * This service provides secure token storage and management for the application.
 * Access tokens are stored in memory only to prevent XSS attacks.
 * Refresh tokens will be handled via httpOnly cookies (not stored here).
 *
 * Security features:
 * - No localStorage usage (XSS protection)
 * - Automatic token expiry tracking
 * - Thread-safe token refresh
 *
 * Note: CSRF tokens are managed separately by CsrfProvider, not here.
 */

import { EventEmitter } from 'events';
import { reduxLogger } from '@/core/utils/logger';

interface TokenData {
  accessToken: string | null;
  expiresIn: number; // seconds
  tokenType: string;
  /** Set to true when using cookie-based auth (Stytch) - allows empty/null accessToken */
  isCookieAuth?: boolean;
}

interface TokenServiceEvents {
  'token:refreshed': (token: string) => void;
  'token:expired': () => void;
  'token:cleared': () => void;
  'auth:error': (error: Error) => void;
}

export class TokenService extends EventEmitter {
  private static instance: TokenService;
  
  // Memory-only storage for access token
  private accessToken: string | null = null;
  private tokenExpiry: number | null = null;
  private tokenType: string = 'Bearer';

  // Refresh management
  private refreshPromise: Promise<void> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private visibilityHandler: (() => void) | null = null;

  // Configuration
  private readonly TOKEN_REFRESH_BUFFER = 300; // 5 minutes before expiry to refresh (was 30 seconds)
  private readonly MIN_TOKEN_LIFETIME = 60; // minimum acceptable token lifetime in seconds
  private readonly MAX_REFRESH_BUFFER = 600; // Don't refresh if more than 10 minutes remain
  
  private constructor() {
    super();
    // Prevent memory leaks from unbounded listeners
    this.setMaxListeners(15);

    // When user returns to a background tab, check if token needs refresh.
    // Chrome aggressively throttles setTimeout in background tabs (1 min+),
    // so the scheduled refresh timer may have missed its window.
    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible' && this.accessToken) {
          if (this.isTokenExpired() || this.isTokenExpiring()) {
            reduxLogger.info('[TokenService] Tab became visible — token expired/expiring, triggering refresh');
            this.emit('token:expiring');
          }
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }
  
  /**
   * Get singleton instance
   */
  static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }
  
  /**
   * Set access token with expiry tracking
   */
  setTokens(tokenData: TokenData): void {
    // For cookie-based auth, allow null/empty accessToken as placeholder
    // Requires explicit isCookieAuth flag to prevent accidental misuse
    if (tokenData.isCookieAuth && (tokenData.accessToken === null || tokenData.accessToken === '')) {
      // This is a placeholder for cookie-based authentication (Stytch)
      this.accessToken = 'cookie_auth_placeholder';
      this.tokenType = tokenData.tokenType || 'Bearer';
      
      // Calculate and store expiry time
      const now = Date.now();
      this.tokenExpiry = now + (tokenData.expiresIn * 1000);
      
      // Set up automatic refresh timer
      this.scheduleTokenRefresh(tokenData.expiresIn);
      
      // Emit event with placeholder
      this.emit('token:refreshed', this.accessToken);
      
      reduxLogger.info('[TokenService] Cookie-based auth placeholder set with expiry:', new Date(this.tokenExpiry).toISOString());
      return;
    }
    
    // Validate token data for real tokens
    if (!tokenData.accessToken || !tokenData.expiresIn) {
      throw new Error('Invalid token data provided');
    }
    
    if (tokenData.expiresIn < this.MIN_TOKEN_LIFETIME) {
      reduxLogger.warn(`Token lifetime (${tokenData.expiresIn}s) is below minimum (${this.MIN_TOKEN_LIFETIME}s)`);
    }
    
    // Store token in memory
    this.accessToken = tokenData.accessToken;
    this.tokenType = tokenData.tokenType || 'Bearer';
    
    // Calculate and store expiry time
    const now = Date.now();
    this.tokenExpiry = now + (tokenData.expiresIn * 1000);
    
    // Set up automatic refresh timer
    this.scheduleTokenRefresh(tokenData.expiresIn);
    
    // Emit event
    this.emit('token:refreshed', this.accessToken);
    
    reduxLogger.info('[TokenService] Token set with expiry:', new Date(this.tokenExpiry).toISOString());
  }
  
  /**
   * Get current access token if valid
   */
  getAccessToken(): string | null {
    // Check if token exists
    if (!this.accessToken || !this.tokenExpiry) {
      return null;
    }
    
    // Check if token is expired
    if (this.isTokenExpired()) {
      this.clearTokens();
      this.emit('token:expired');
      return null;
    }
    
    return this.accessToken;
  }
  
  /**
   * Get authorization header value
   */
  getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    // Don't return auth header for cookie-based auth placeholder
    if (token === 'cookie_auth_placeholder') {
      return null;
    }
    return token ? `${this.tokenType} ${token}` : null;
  }
  
  /**
   * Clear all tokens from memory
   */
  clearTokens(): void {
    this.accessToken = null;
    this.tokenExpiry = null;
    this.cancelRefreshTimer();
    this.refreshPromise = null;
    this.emit('token:cleared');
    reduxLogger.info('[TokenService] Tokens cleared');
  }
  
  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    if (!this.tokenExpiry) {
      return true;
    }
    return Date.now() >= this.tokenExpiry;
  }
  
  /**
   * Check if token is expiring soon
   */
  isTokenExpiring(bufferSeconds: number = this.TOKEN_REFRESH_BUFFER): boolean {
    if (!this.tokenExpiry) {
      return true;
    }
    
    const bufferMs = bufferSeconds * 1000;
    return Date.now() >= (this.tokenExpiry - bufferMs);
  }
  
  /**
   * Get time until token expiry in seconds
   */
  getTimeUntilExpiry(): number | null {
    if (!this.tokenExpiry) {
      return null;
    }
    
    const msUntilExpiry = this.tokenExpiry - Date.now();
    return Math.max(0, Math.floor(msUntilExpiry / 1000));
  }
  
  /**
   * Set refresh promise to prevent concurrent refreshes
   */
  setRefreshPromise(promise: Promise<void>): void {
    this.refreshPromise = promise;
    // Notify subscribers that a refresh started — keeps React tokenStatus.hasRefreshInProgress current
    this.emit('token:refreshing');
    // The .finally() creates a derived promise that also rejects when the
    // original rejects. Without .catch(), this derived promise would surface
    // as an unhandled rejection in Sentry — even though the caller already
    // handles the original promise's rejection via await + .catch().
    promise.finally(() => {
      this.refreshPromise = null;
      // Notify subscribers that refresh settled (success or failure)
      this.emit('token:refresh-settled');
    }).catch(() => {
      // Intentionally suppressed — the original promise rejection is
      // handled by SecureAuthService.refreshAccessToken's caller.
    });
  }
  
  /**
   * Get current refresh promise if exists
   */
  getRefreshPromise(): Promise<void> | null {
    return this.refreshPromise;
  }
  
  /**
   * Schedule automatic token refresh with intelligent timing
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    // Cancel existing timer
    this.cancelRefreshTimer();
    
    // For tokens with 4-hour lifetime (14400 seconds), refresh at 3h 55min (14100 seconds)
    // For shorter tokens, use proportional timing
    let refreshBuffer = this.TOKEN_REFRESH_BUFFER;
    
    // Adjust buffer based on token lifetime
    if (expiresIn <= 3600) { // 1 hour or less
      refreshBuffer = Math.min(60, expiresIn * 0.1); // 10% or 1 minute, whichever is less
    } else if (expiresIn <= 7200) { // 2 hours or less
      refreshBuffer = 120; // 2 minutes
    } else if (expiresIn <= 10800) { // 3 hours or less
      refreshBuffer = 180; // 3 minutes
    }
    // else use default 5 minutes for 4+ hour tokens
    
    // Calculate when to refresh
    const refreshIn = Math.max(0, expiresIn - refreshBuffer);

    if (refreshIn > 0) {
      // Add ±10% jitter to prevent thundering herd on mass token refresh
      // (e.g., after deploys when many tokens expire near-simultaneously)
      const jitter = refreshIn * (0.9 + Math.random() * 0.2); // [0.9, 1.1] × refreshIn
      const jitteredRefreshIn = Math.max(1, Math.round(jitter));

      reduxLogger.info('[TokenService] Scheduling token refresh', {
        expiresIn: `${expiresIn}s (${(expiresIn / 3600).toFixed(1)}h)`,
        refreshIn: `${jitteredRefreshIn}s (${(jitteredRefreshIn / 3600).toFixed(1)}h)`,
        buffer: `${refreshBuffer}s`,
        jitter: `${Math.round(((jitteredRefreshIn - refreshIn) / refreshIn) * 100)}%`,
      });

      this.refreshTimer = setTimeout(() => {
        reduxLogger.info('[TokenService] Token refresh timer triggered');
        // The actual refresh will be handled by SecureAuthService
        this.emit('token:expiring');
      }, jitteredRefreshIn * 1000);
    } else {
      // Token is already expiring, trigger immediate refresh
      reduxLogger.warn('[TokenService] Token already expiring, triggering immediate refresh');
      this.emit('token:expiring');
    }
  }
  
  /**
   * Cancel refresh timer
   */
  private cancelRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Get service status for debugging
   */
  getStatus(): {
    hasToken: boolean;
    isExpired: boolean;
    isExpiring: boolean;
    timeUntilExpiry: number | null;
    hasRefreshInProgress: boolean;
  } {
    return {
      hasToken: !!this.accessToken,
      isExpired: this.accessToken ? this.isTokenExpired() : false,
      isExpiring: this.accessToken ? this.isTokenExpiring() : false,
      timeUntilExpiry: this.accessToken ? this.getTimeUntilExpiry() : null,
      hasRefreshInProgress: !!this.refreshPromise,
    };
  }
  
  /**
   * Clean up resources
   */
  destroy(): void {
    this.clearTokens();
    if (typeof document !== 'undefined' && this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    this.removeAllListeners();
  }
}

// Export singleton instance
export const tokenService = TokenService.getInstance();

// Export types
export type { TokenData, TokenServiceEvents };
